"use client";
import React, { useState, KeyboardEvent, useEffect } from "react";
import { EventBySlugOutput } from "~/types/EventTypesInferred";
import { api, RouterOutputs } from "~/utils/api";
import { toast } from "react-toastify";
import { FaTrash } from "react-icons/fa";
import { Avatar, AvatarFallback, AvatarImage } from "../shadcn/Avatar";
import { GoPersonFill } from "react-icons/go";
import { useSession } from "next-auth/react";
import { Message, Prisma, Role } from "@prisma/client";
import { GetEventMessagesOutput } from "~/types/ConversationTypes";
import { slugifyUser } from "~/utils/string";
import { cn } from "~/utils/fontsConfig";
import Link from "next/link";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription,
} from "~/components/v3/shadcn/Card";
import { Button } from "../themed/Button";
import { TextArea } from "../themed/TextArea";
import { If } from "../Conditional";
import { LoginControlButton } from "../themed/LoginControlButton";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "~/components/shadcn/Tabs";
import AdHocMessengerManager from "~/components/themed/AdHocMessengerManager";
import { RiArrowRightCircleLine } from "react-icons/ri";

type Props = {
    event: Prisma.EventGetPayload<{
        select: {
            id: true;
            slug: true;
            eventJourneyStatus: true;
            attendees: {
                select: { status: true };
            };
            hosts: {
                select: {
                    user: { select: { id: true } };
                };
            };
            communityId: true;
            community: {
                select: {
                    id: true;
                    isHostAllowedToSMS: true;
                    isHostAllowedToEmail: true;
                };
            };
        };
    }>;
    permissions?: RouterOutputs["permissions"]["getUserPermissions"];
    eventMessageHistoryRSC: React.ReactNode;
};

const EventChat = ({ event, permissions, eventMessageHistoryRSC }: Props) => {
    if (!event || !permissions) return null;
    const { data: session } = useSession();

    const { data: userCanChat, isLoading: isLoadingUserCanChat } =
        api.conversation.canViewEventChat.useQuery({
            eventId: event.id,
        });

    const hostIds = event.hosts.map((h) => h.user.id);
    const [canAccessBlast, setCanAccessBlast] = useState(
        permissions?.isManager ||
            permissions?.isHost ||
            permissions?.isPreapprovedHost ||
            permissions?.isAdmin,
    );

    const [message, setMessage] = useState<string>("");

    // Determine if the user is allowed to delete a message in the conversation
    const canDeleteMessage = (message: Message) => {
        // Check if user is logged in
        if (!session) {
            return false;
        }

        // Check for admin role
        if (session.user.role === Role.ADMIN) {
            return true;
        }

        // Check if user is a manager of the event's community
        if (event.community && permissions?.isManager) {
            return true;
        }

        // Check if user is the author of the message
        if (message.userId === session.user.id) {
            return true;
        }

        // Default to not enabling delete
        return false;
    };

    const {
        data: conversationMessages,
        isLoading: isLoadingConversationMessages,
        refetch: refetchConversationMessages,
    } = api.conversation.getEventMessages.useQuery(
        {
            eventId: event.id,
        },
        {
            enabled:
                !!event.id && !!userCanChat?.showChat && !!session?.user.id,
            refetchInterval: 180000, // 3 minutes in miliseconds
        },
    );
    const [chatMessages, setChatMessages] = useState<GetEventMessagesOutput>(
        [],
    );
    const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
    useEffect(() => {
        // replace the chat messages with new messages from conversation Messages
        // when conversation messages changes. Do this in place.
        // This is to prevent the chat from scrolling to the top every time a new message is sent.
        // This also deletes any messages that were deleted from the conversation.
        if (conversationMessages) {
            const newMessages = conversationMessages.filter(
                (newMsg) =>
                    !chatMessages.some(
                        (existingMsg) => existingMsg.id === newMsg.id,
                    ),
            );
            const deletedMessages = chatMessages.filter(
                (existingMsg) =>
                    !conversationMessages.some(
                        (newMsg) => newMsg.id === existingMsg.id,
                    ),
            );

            // remove deleted messages
            if (deletedMessages.length > 0) {
                setChatMessages(
                    chatMessages.filter(
                        (existingMsg) =>
                            !deletedMessages.some(
                                (deletedMsg) =>
                                    deletedMsg.id === existingMsg.id,
                            ),
                    ),
                );
            }

            if (newMessages.length > 0) {
                setChatMessages([...chatMessages, ...newMessages]);
            }

            // only show skeleton on first fetch
            if (isInitialLoad) {
                setIsInitialLoad(false);
            }
        }
    }, [conversationMessages]);

    const sendMessageMutation = api.conversation.sendEventMessage.useMutation();
    const handleSendMessage = async () => {
        toast.loading("Sending message...", {
            toastId: "send-message",
        });

        await sendMessageMutation.mutateAsync(
            {
                eventId: event.id,
                message: message,
            },
            {
                onSuccess: () => {
                    toast.success("Message sent!", {
                        toastId: "send-message",
                    });
                    setMessage("");
                },
                onError: () => {
                    toast.error("Error sending message!", {
                        toastId: "send-message",
                    });
                },
                onSettled: () => {
                    toast.dismiss("send-message");
                    refetchConversationMessages();
                },
            },
        );
    };
    const deleteMessageMutation = api.conversation.deleteMessage.useMutation();
    const handleDeleteMessage = async (messageId: string) => {
        toast.loading("Deleting message...", {
            toastId: "delete-message",
        });

        await deleteMessageMutation.mutateAsync(
            {
                messageId: messageId,
            },
            {
                onSuccess: () => {
                    toast.success("Message deleted!", {
                        autoClose: 1000,
                    });
                    refetchConversationMessages();
                },
                onError: () => {
                    toast.error("Error deleting message!");
                },
                onSettled: () => {
                    toast.dismiss("delete-message");
                },
            },
        );
    };

    const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const isHost = (user?: { id: string } | null) => {
        if (!user) return false;
        return hostIds.includes(user.id);
    };

    if (!userCanChat?.showChat) {
        return (
            <Card className="p-0 pt-2 lg:px-1 lg:py-2">
                <CardHeader className="p-0 py-4 lg:p-4">
                    <CardTitle>Event Chat</CardTitle>
                    <CardDescription>{userCanChat?.message}</CardDescription>
                    <If condition={!session?.user}>
                        <LoginControlButton
                            className="md:flex-row"
                            session={session}
                        />
                    </If>
                </CardHeader>
                <CardContent className="bg-opacity-60 p-0 blur-md backdrop-blur-md lg:p-4">
                    <CardContent className="p-0 lg:p-4">
                        <div className="flex flex-col space-y-4">
                            <div className="flex-1 space-y-4 py-1">
                                <div className="h-4 w-3/4 rounded bg-gray-200"></div>
                                <div className="space-y-2">
                                    <div className="h-4 rounded bg-gray-200"></div>
                                    <div className="h-4 w-5/6 rounded bg-gray-200"></div>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="lg:px-1 lg:py-2">
            <CardHeader className="relative p-0 py-4 lg:p-4">
                <CardTitle>Event Messaging</CardTitle>
                <Link
                    href={`/events/${event.slug}/send-messages`}
                    className="absolute right-2 top-2 z-10 flex items-center justify-center p-[5px] text-v3-N300 hover:text-v3-N200"
                >
                    <RiArrowRightCircleLine className="h-4 w-4" />
                    <span className="sr-only">More</span>
                </Link>
            </CardHeader>
            <CardContent className="p-0 lg:p-4">
                <Tabs defaultValue="chat" className="w-full">
                    <TabsList className="flex h-auto w-full justify-start rounded-none border-b-[1.5px] border-[#363A41] p-0">
                        <TabsTrigger
                            className="rounded-none p-2 data-[state=active]:border-b-[2px] data-[state=active]:border-v3-C400 data-[state=active]:bg-[#121418] lg:px-8 lg:py-5"
                            value="chat"
                        >
                            Chat
                        </TabsTrigger>
                        <If condition={canAccessBlast ?? false}>
                            <TabsTrigger
                                className="rounded-none p-2 data-[state=active]:border-b-[2px] data-[state=active]:border-v3-C400 data-[state=active]:bg-[#121418] lg:px-8 lg:py-5"
                                value="blast"
                            >
                                Message blast
                            </TabsTrigger>
                        </If>
                        <If condition={canAccessBlast ?? false}>
                            <TabsTrigger
                                className="rounded-none p-2 data-[state=active]:border-b-[2px] data-[state=active]:border-v3-C400 data-[state=active]:bg-[#121418] lg:px-8 lg:py-5"
                                value="blast-history"
                            >
                                Blast history
                            </TabsTrigger>
                        </If>
                    </TabsList>

                    <TabsContent value="chat" className="mt-6">
                        {isLoadingConversationMessages && isInitialLoad ? (
                            <div className="flex animate-pulse flex-col space-y-4">
                                <div className="flex-1 space-y-4 py-1">
                                    <div className="h-4 w-3/4 rounded bg-gray-200"></div>
                                    <div className="space-y-2">
                                        <div className="h-4 rounded bg-gray-200"></div>
                                        <div className="h-4 w-5/6 rounded bg-gray-200"></div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <>
                                <CardDescription className="mb-4 flex text-sm text-gray-400">
                                    Ask questions and talk with the hosts /
                                    other attendees!
                                </CardDescription>
                                <div
                                    className={cn(
                                        "max-h-[200px] max-w-full space-y-5 overflow-y-scroll",
                                        chatMessages?.length ? "pb-10" : "",
                                    )}
                                >
                                    {chatMessages?.map((message) => (
                                        <div
                                            key={message.id}
                                            className="flex w-full items-center justify-between"
                                        >
                                            <div className="flex flex-grow items-center space-x-3">
                                                <Avatar>
                                                    <AvatarImage
                                                        src={
                                                            message?.user
                                                                ?.image || ""
                                                        }
                                                        alt="User Image"
                                                    />
                                                    <AvatarFallback className="bg-secondary">
                                                        <GoPersonFill
                                                            className="h-5 w-5 text-primary"
                                                            aria-hidden="true"
                                                        />
                                                    </AvatarFallback>
                                                </Avatar>
                                                <div className="flex flex-grow flex-col">
                                                    <Link
                                                        href={
                                                            message?.user
                                                                ? `/profile/${slugifyUser(
                                                                      {
                                                                          name: message
                                                                              .user
                                                                              .name,
                                                                          id: message
                                                                              .user
                                                                              .id,
                                                                          slug: message
                                                                              .user
                                                                              .slug,
                                                                      },
                                                                  )}`
                                                                : "#"
                                                        }
                                                        target="_blank"
                                                        className={cn(
                                                            `
                                                    cursor-pointer
                                                    text-xs
                                                    text-middleground
                                                    hover:underline
                                                    `,
                                                            isHost(
                                                                message?.user,
                                                            ) &&
                                                                "text-secondary",
                                                        )}
                                                    >
                                                        {isHost(
                                                            message?.user,
                                                        ) && "Host - "}
                                                        {message?.user?.name}
                                                    </Link>
                                                    <p
                                                        className={cn(
                                                            message.text
                                                                ?.length > 30
                                                                ? "max-w-[500px] break-words break-all"
                                                                : "",
                                                        )}
                                                    >
                                                        {message.text}
                                                    </p>
                                                </div>
                                            </div>
                                            {canDeleteMessage(message) && (
                                                <div
                                                    className="mr-2 flex flex-shrink-0 hover:cursor-pointer hover:text-v2-cta"
                                                    onClick={() => {
                                                        handleDeleteMessage(
                                                            message.id,
                                                        );
                                                    }}
                                                >
                                                    <FaTrash />
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                                {chatMessages.length < 200 && (
                                    <div className="mt-4 flex w-full flex-col space-y-4">
                                        <TextArea
                                            placeholder="Type a message..."
                                            className="h-[50px] w-full placeholder:not-italic"
                                            value={message}
                                            onChange={(e) =>
                                                setMessage(e.target.value)
                                            }
                                            onKeyDown={handleKeyPress}
                                        />
                                        <Button
                                            variant="secondary"
                                            className="ml-4 w-[150px] self-end px-4 py-2"
                                            onClick={handleSendMessage}
                                        >
                                            Send message
                                        </Button>
                                    </div>
                                )}
                            </>
                        )}
                    </TabsContent>

                    <TabsContent value="blast" className="mt-6">
                        <AdHocMessengerManager
                            event={event}
                            hasPermissions={canAccessBlast ?? false}
                        />
                    </TabsContent>
                    <TabsContent value="blast-history" className="mt-6">
                        {eventMessageHistoryRSC}
                    </TabsContent>
                </Tabs>
            </CardContent>
        </Card>
    );
};

export default EventChat;
